// ---------------------------------------------------------------------------
// layout-reducer — pure transforms over { order, hidden }
// ---------------------------------------------------------------------------
//
// Every dashboard layout mutation is a pure function here; the controller is the
// only stateful caller. Keeps the reorder/visibility logic single-owned and
// fully unit-testable. `normalizeLayout` sanitizes untrusted persisted input.

/**
 * @typedef {import('../../contracts/card-grid.js').CardGridLayout} CardGridLayout
 * @typedef {import('../../contracts/card-grid.js').CardDescriptor} CardDescriptor
 */

/** Dedupe an array preserving first occurrence. @param {string[]} arr */
function dedupe(arr) {
  const seen = new Set();
  const out = [];
  for (const v of arr) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/**
 * Clamp a value to an integer within [min, max]; returns NaN if not finite.
 * @param {unknown} value @param {number} min @param {number} max @returns {number}
 */
function clampInt(value, min, max) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return NaN;
  return Math.min(max, Math.max(min, n));
}

/**
 * The descriptor default size for an id (colSpan/rowSpan default to 1).
 * @param {Map<string, {colSpan?: number, rowSpan?: number}>} defaultsById
 * @param {string} id
 * @returns {{colSpan: number, rowSpan: number}}
 */
function defaultSize(defaultsById, id) {
  const d = defaultsById?.get(id);
  return { colSpan: d?.colSpan ?? 1, rowSpan: d?.rowSpan ?? 1 };
}

/**
 * Sanitize a raw persisted/loaded layout against the known card set:
 *  - dedupe both lists (first occurrence wins)
 *  - drop ids not in knownIds (a card that no longer exists)
 *  - if an id is in both order and hidden, hidden wins (removed from order)
 *  - append known ids missing from order (a newly-shipped card the stored
 *    layout never saw) at the end, visible
 *  - sizes: keep only known ids; clamp colSpan/rowSpan to the card's PER-CARD max
 *    (from `maxById`; default 4/2) (skip NaN); drop entries equal to the
 *    descriptor default (minimal storage)
 *  - non-hideable cards are removed from `hidden` (forced visible) even if a
 *    stale/tampered layout listed them — `hideable:false` is a hard guarantee
 *
 * @param {{ order?: string[], hidden?: string[], sizes?: Record<string, {colSpan?: number, rowSpan?: number}> }} raw
 * @param {string[]} knownIds - in registration order.
 * @param {Map<string, {colSpan?: number, rowSpan?: number}>} [defaultsById]
 * @param {Set<string>} [nonHideableIds] - ids that must never be hidden.
 * @param {Map<string, {colMax: number, rowMax: number}>} [maxById] - per-card resize maxima.
 * @returns {CardGridLayout}
 */
export function normalizeLayout(raw, knownIds, defaultsById = new Map(), nonHideableIds = new Set(), maxById = new Map()) {
  const known = new Set(knownIds);
  const hidden = dedupe(
    (raw?.hidden ?? []).filter((id) => known.has(id) && !nonHideableIds.has(id)),
  );
  const hiddenSet = new Set(hidden);
  const order = dedupe((raw?.order ?? []).filter((id) => known.has(id) && !hiddenSet.has(id)));
  // Append known-but-missing ids (visible), preserving registration order.
  const inOrder = new Set(order);
  for (const id of knownIds) {
    if (!inOrder.has(id) && !hiddenSet.has(id)) order.push(id);
  }

  /** @type {Record<string, import('../../contracts/card-grid.js').CardGridSize>} */
  const sizes = {};
  const rawSizes = raw?.sizes;
  // Only a plain (non-array) object; ignore arrays/scalars/null.
  if (rawSizes && typeof rawSizes === 'object' && !Array.isArray(rawSizes)) {
    for (const id of Object.keys(rawSizes)) {
      if (!known.has(id)) continue;
      const m = maxById.get(id) || { colMax: 4, rowMax: 2 };
      const colSpan = clampInt(rawSizes[id]?.colSpan, 1, m.colMax);
      const rowSpan = clampInt(rawSizes[id]?.rowSpan, 1, m.rowMax);
      if (Number.isNaN(colSpan) || Number.isNaN(rowSpan)) continue;
      const def = defaultSize(defaultsById, id);
      if (colSpan === def.colSpan && rowSpan === def.rowSpan) continue; // == default → omit
      sizes[id] = /** @type {import('../../contracts/card-grid.js').CardGridSize} */ ({ colSpan, rowSpan });
    }
  }

  return { order, hidden, sizes };
}

/**
 * Set (or clear) a card's size. Clamps to the card's per-card max (default 4/2);
 * deletes the entry when it equals the descriptor default (minimal storage +
 * stable round-trip). Returns the same layout reference when nothing changes
 * (no-op for the controller guard).
 *
 * @param {CardGridLayout} layout
 * @param {string} id
 * @param {{colSpan: number, rowSpan: number}} size
 * @param {Map<string, {colSpan?: number, rowSpan?: number}>} defaultsById
 * @param {{colMax: number, rowMax: number}} [max] - this card's resize maxima (default 4/2).
 * @returns {CardGridLayout}
 */
export function setCardSize(layout, id, size, defaultsById, max = { colMax: 4, rowMax: 2 }) {
  const colSpan = clampInt(size.colSpan, 1, max.colMax);
  const rowSpan = clampInt(size.rowSpan, 1, max.rowMax);
  if (Number.isNaN(colSpan) || Number.isNaN(rowSpan)) return layout;
  const def = defaultSize(defaultsById, id);
  const current = layout.sizes[id];
  const wantDefault = colSpan === def.colSpan && rowSpan === def.rowSpan;

  if (wantDefault) {
    if (!current) return layout; // already default → no-op
    const sizes = { ...layout.sizes };
    delete sizes[id];
    return { ...layout, sizes };
  }
  if (current && current.colSpan === colSpan && current.rowSpan === rowSpan) return layout; // no change
  return { ...layout, sizes: { ...layout.sizes, [id]: /** @type {import('../../contracts/card-grid.js').CardGridSize} */ ({ colSpan, rowSpan }) } };
}

/**
 * Effective size for a card = persisted override ?? descriptor default.
 *
 * @param {Record<string, import('../../contracts/card-grid.js').CardGridSize>} sizes
 * @param {string} id
 * @param {{colSpan?: number, rowSpan?: number}} descriptor
 * @returns {{colSpan: number, rowSpan: number}}
 */
export function effectiveSize(sizes, id, descriptor) {
  return sizes?.[id] ?? { colSpan: descriptor?.colSpan ?? 1, rowSpan: descriptor?.rowSpan ?? 1 };
}

/**
 * Move `fromKey` to occupy the current slot of `toKey` (remove-then-insert).
 * Dragging downward lands after the target, upward lands before — natural
 * because removal shifts indices. No-op (same reference contents) when keys are
 * equal or absent.
 *
 * @param {string[]} order
 * @param {string} fromKey
 * @param {string} toKey
 * @returns {string[]}
 */
export function moveCard(order, fromKey, toKey) {
  if (fromKey === toKey) return order;
  if (order.indexOf(fromKey) < 0 || order.indexOf(toKey) < 0) return order;
  return rebuildMove(order, fromKey, toKey);
}

/**
 * Deterministic remove-then-insert-at-target-index. Extracted so the index math
 * is unambiguous: compute the target index in the ORIGINAL array, remove the
 * dragged item, then insert at that same index (clamped).
 *
 * @param {string[]} order
 * @param {string} fromKey
 * @param {string} toKey
 * @returns {string[]}
 */
function rebuildMove(order, fromKey, toKey) {
  const from = order.indexOf(fromKey);
  const target = order.indexOf(toKey);
  const next = order.slice();
  next.splice(from, 1);
  const insertAt = Math.min(target, next.length);
  next.splice(insertAt, 0, fromKey);
  return next;
}

/**
 * Move a card up one slot in the VISIBLE sequence (the order minus hidden), so
 * keyboard moves match what the user sees. Boundary-safe (no-op at the top).
 *
 * @param {string[]} order
 * @param {string[]} hidden
 * @param {string} id
 * @returns {string[]}
 */
export function moveUp(order, hidden, id) {
  return moveWithinVisible(order, hidden, id, -1);
}

/**
 * Move a card down one slot in the visible sequence. Boundary-safe.
 * @param {string[]} order @param {string[]} hidden @param {string} id @returns {string[]}
 */
export function moveDown(order, hidden, id) {
  return moveWithinVisible(order, hidden, id, +1);
}

/**
 * @param {string[]} order
 * @param {string[]} hidden
 * @param {string} id
 * @param {number} delta - -1 up, +1 down.
 * @returns {string[]}
 */
function moveWithinVisible(order, hidden, id, delta) {
  const hiddenSet = new Set(hidden);
  const visible = order.filter((k) => !hiddenSet.has(k));
  const vi = visible.indexOf(id);
  const target = vi + delta;
  if (vi < 0 || target < 0 || target >= visible.length) return order;
  const neighborId = visible[target];
  return rebuildMove(order, id, neighborId);
}

/**
 * Hide a card (add to hidden, remove from order). Idempotent.
 *
 * @param {CardGridLayout} layout
 * @param {string} id
 * @returns {CardGridLayout}
 */
export function hideCard(layout, id) {
  if (layout.hidden.includes(id)) return layout;
  return {
    ...layout,
    order: layout.order.filter((k) => k !== id),
    hidden: [...layout.hidden, id],
  };
}

/**
 * Show a hidden card (remove from hidden, append to order end). Idempotent.
 *
 * @param {CardGridLayout} layout
 * @param {string} id
 * @returns {CardGridLayout}
 */
export function showCard(layout, id) {
  if (!layout.hidden.includes(id)) return layout;
  return {
    ...layout,
    order: layout.order.includes(id) ? layout.order : [...layout.order, id],
    hidden: layout.hidden.filter((k) => k !== id),
  };
}

/**
 * Derive the ordered, visible descriptor list from a NORMALIZED layout.
 *
 * @param {string[]} order
 * @param {string[]} hidden
 * @param {Map<string, CardDescriptor>} byId
 * @returns {CardDescriptor[]}
 */
export function deriveVisible(order, hidden, byId) {
  const hiddenSet = new Set(hidden);
  /** @type {CardDescriptor[]} */
  const out = [];
  for (const id of order) {
    if (hiddenSet.has(id)) continue;
    const d = byId.get(id);
    if (d) out.push(d);
  }
  return out;
}

/**
 * The hidden-menu entries: known ids that are currently hidden, in hidden order.
 *
 * @param {string[]} hidden
 * @param {Map<string, CardDescriptor>} byId
 * @returns {CardDescriptor[]}
 */
export function deriveHidden(hidden, byId) {
  /** @type {CardDescriptor[]} */
  const out = [];
  for (const id of hidden) {
    const d = byId.get(id);
    if (d) out.push(d);
  }
  return out;
}

/**
 * Structural equality of two layouts (order + hidden position-sensitive; sizes
 * by key set + spans). MUST include sizes — the controller uses this as the
 * no-op guard, so omitting sizes would let a same-span resize churn rerender +
 * persist. Tolerates a missing `sizes` (treated as `{}`).
 *
 * @param {{ order: string[], hidden: string[], sizes?: Record<string, import('../../contracts/card-grid.js').CardGridSize> }} a
 * @param {{ order: string[], hidden: string[], sizes?: Record<string, import('../../contracts/card-grid.js').CardGridSize> }} b
 * @returns {boolean}
 */
export function layoutEquals(a, b) {
  return arrEq(a.order, b.order) && arrEq(a.hidden, b.hidden) && sizesEqual(a.sizes, b.sizes);
}

/** @param {string[]} a @param {string[]} b */
function arrEq(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * @param {Record<string, import('../../contracts/card-grid.js').CardGridSize> | undefined} a
 * @param {Record<string, import('../../contracts/card-grid.js').CardGridSize> | undefined} b
 */
function sizesEqual(a, b) {
  const aa = a ?? {};
  const bb = b ?? {};
  const ak = Object.keys(aa);
  if (ak.length !== Object.keys(bb).length) return false;
  for (const k of ak) {
    const av = aa[k];
    const bv = bb[k];
    if (!bv || av.colSpan !== bv.colSpan || av.rowSpan !== bv.rowSpan) return false;
  }
  return true;
}
